import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

const STORAGE_PREFIX = "scrollPos:";
const MAX_RESTORE_ATTEMPTS = 40; // ~0.6s at 60fps - covers async data still loading in.

function readScroll(key) {
  try {
    const value = sessionStorage.getItem(STORAGE_PREFIX + key);
    return value === null ? null : Number(value);
  } catch (error) {
    return null;
  }
}

function writeScroll(key, value) {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + key, String(value));
  } catch (error) {
    // sessionStorage unavailable (private mode, quota, etc.) - scroll just won't be restored.
  }
}

// Scroll position per pathname, kept in sessionStorage (survives reloads,
// cleared when the tab closes). Keyed by pathname rather than the history
// entry so it restores both on a real browser back/forward AND on an
// ordinary link click back to the same page (e.g. a breadcrumb), which
// React Router treats as a fresh PUSH, not a POP.
//
// Uses useLayoutEffect (not useEffect) so the outgoing page's "scroll"
// listener is detached synchronously, before the browser's own scroll
// clamping (which fires when the new, often shorter, page content is
// swapped in) can dispatch a stray scroll event into it and stomp the
// saved position.
//
// The target scroll is retried across a few animation frames because the
// destination page's real content (e.g. a places list) often loads in
// asynchronously - the very first frame is just a loader, so a single
// scrollTo would get silently clamped back to 0 before the page has grown
// tall enough to actually hold that position.
export default function useScrollRestoration() {
  const location = useLocation();

  useLayoutEffect(() => {
    const key = location.pathname;
    const target = readScroll(key) ?? 0;
    let cancelled = false;
    let attempts = 0;
    let frame = null;

    function handleScroll() {
      writeScroll(key, window.scrollY);
    }

    function tryRestore() {
      if (cancelled) return;
      window.scrollTo({ top: target, behavior: "instant" });
      attempts += 1;

      if (Math.abs(window.scrollY - target) > 1 && attempts < MAX_RESTORE_ATTEMPTS) {
        frame = requestAnimationFrame(tryRestore);
      } else {
        window.addEventListener("scroll", handleScroll, { passive: true });
      }
    }

    tryRestore();

    return () => {
      cancelled = true;
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [location.pathname]);
}
